import type { RunDetail, RunEvent, RunPhase, RunStatus } from '@/entities/sender.entity';
import { formatScheduleAbortReason } from '@/entities/sender-events';

export type PipelineStepId =
	| 'queue'
	| 'session'
	| 'prep'
	| 'scheduling'
	| 'wait_send'
	| 'cleanup'
	| 'done';

export type PipelineStepState =
	| 'pending'
	| 'active'
	| 'completed'
	| 'skipped'
	| 'failed'
	| 'cancelled';

export interface PipelineProgress {
	value: number;
	max: number;
}

export interface PipelineHero {
	title: string;
	subtitle?: string;
	eta?: string;
	progress?: PipelineProgress;
	variant: 'active' | 'success' | 'warning' | 'error' | 'muted';
}

export interface PipelineNextStep {
	label: string;
}

export interface PipelineStep {
	id: PipelineStepId;
	label: string;
	state: PipelineStepState;
	durationMs?: number;
	durationLabel?: string;
}

export interface RunPipelineView {
	hero: PipelineHero;
	nextSteps: PipelineNextStep[];
	steps: PipelineStep[];
	isLive: boolean;
	activeStepId: PipelineStepId | null;
}

const STEP_DEFINITIONS: ReadonlyArray<{ id: PipelineStepId; label: string }> = [
	{ id: 'queue', label: 'Очередь' },
	{ id: 'session', label: 'Сессия' },
	{ id: 'prep', label: 'Подготовка' },
	{ id: 'scheduling', label: 'Планирование в TG' },
	{ id: 'wait_send', label: 'Ожидание отправки' },
	{ id: 'cleanup', label: 'Самоудаление' },
	{ id: 'done', label: 'Готово' },
];

export const TERMINAL_RUN_STATUSES: ReadonlySet<RunStatus> = new Set([
	'completed',
	'completed_empty',
	'completed_with_warnings',
	'failed',
	'cancelled',
]);

export function isRunTerminalStatus(status: RunStatus): boolean {
	return TERMINAL_RUN_STATUSES.has(status);
}

const SKIPPABLE_STEP_IDS: ReadonlySet<PipelineStepId> = new Set([
	'scheduling',
	'wait_send',
	'cleanup',
]);

export interface ParsedRunEvents {
	runStartedAt?: number;
	audienceResolvedAt?: number;
	burstScheduledAt?: number;
	scheduleCompleteAt?: number;
	deliveryCompleteAt?: number;
	cleanupStartedAt?: number;
	cleanupSkipped?: boolean;
	accountDeleteEnabled?: boolean;
	accountDeleted?: boolean;
	runCompletedAt?: number;
	scheduleDateMs?: number;
	leadTimeSec?: number;
	postingEstimateSec?: number;
	targetCount?: number;
	lastScheduleProgress?: { scheduled: number; target: number };
	lastDeliveryProgress?: { delivered: number; scheduled: number };
	burstFiredAt?: number;
	partialSchedule?: boolean;
	abortReason?: string;
	scheduledAtAbort?: number;
}

type ParsedEvents = ParsedRunEvents;

function asNumber(value: unknown): number | undefined {
	return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asString(value: unknown): string | undefined {
	return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function asBoolean(value: unknown): boolean | undefined {
	return typeof value === 'boolean' ? value : undefined;
}

function parseTime(value: string | null | undefined): number | undefined {
	if (!value) return undefined;
	const ms = Date.parse(value);
	return Number.isFinite(ms) ? ms : undefined;
}

function parseEvents(events: RunEvent[]): ParsedEvents {
	const sorted = [...events].sort(
		(a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt),
	);
	const parsed: ParsedEvents = {};

	for (const event of sorted) {
		const at = parseTime(event.occurredAt);
		const payload = event.payload;

		switch (event.eventType) {
			case 'run_started':
				if (at != null) parsed.runStartedAt = at;
				if (asBoolean(payload.account_delete_enabled) === false) {
					parsed.accountDeleteEnabled = false;
				}
				break;
			case 'run_resumed':
				if (at != null) parsed.runStartedAt = at;
				break;
			case 'audience_resolved':
				if (at != null) parsed.audienceResolvedAt = at;
				parsed.targetCount = asNumber(payload.target_count) ?? parsed.targetCount;
				break;
			case 'burst_scheduled': {
				if (at != null) parsed.burstScheduledAt = at;
				parsed.leadTimeSec = asNumber(payload.lead_time_sec);
				parsed.postingEstimateSec = asNumber(payload.posting_estimate_sec);
				parsed.targetCount = asNumber(payload.target_count) ?? parsed.targetCount;
				const scheduleFromPayload = parseTime(asString(payload.schedule_date));
				if (scheduleFromPayload != null) parsed.scheduleDateMs = scheduleFromPayload;
				break;
			}
			case 'schedule_date_adjusted': {
				const adjusted = parseTime(asString(payload.adjusted));
				if (adjusted != null) parsed.scheduleDateMs = adjusted;
				break;
			}
			case 'schedule_progress':
				parsed.lastScheduleProgress = {
					scheduled: asNumber(payload.scheduled_count) ?? 0,
					target: asNumber(payload.target_count) ?? 0,
				};
				break;
			case 'schedule_complete':
				if (at != null) parsed.scheduleCompleteAt = at;
				if (
					asBoolean(payload.partial_schedule) ||
					(asNumber(payload.skipped_count) ?? 0) > 0
				) {
					parsed.partialSchedule = true;
				}
				break;
			case 'schedule_aborted':
				parsed.partialSchedule = true;
				parsed.abortReason = asString(payload.reason) ?? parsed.abortReason;
				parsed.scheduledAtAbort =
					asNumber(payload.scheduled_count_at_abort) ?? parsed.scheduledAtAbort;
				break;
			case 'delivery_progress':
				parsed.lastDeliveryProgress = {
					delivered: asNumber(payload.delivered_count) ?? 0,
					scheduled: asNumber(payload.scheduled_count) ?? 0,
				};
				break;
			case 'burst_fired':
				if (at != null) parsed.burstFiredAt = at;
				break;
			case 'delivery_complete':
				if (at != null) parsed.deliveryCompleteAt = at;
				parsed.lastDeliveryProgress = {
					delivered: asNumber(payload.delivered_count) ?? 0,
					scheduled: asNumber(payload.scheduled_count) ?? 0,
				};
				break;
			case 'cleanup_started':
				if (at != null) parsed.cleanupStartedAt = at;
				break;
			case 'cleanup_skipped':
				parsed.cleanupSkipped = true;
				parsed.accountDeleted = false;
				break;
			case 'run_completed':
				if (at != null) parsed.runCompletedAt = at;
				if (asBoolean(payload.partial_schedule)) parsed.partialSchedule = true;
				parsed.abortReason = asString(payload.abort_reason) ?? parsed.abortReason;
				if (payload.account_deleted === false) {
					parsed.accountDeleted = false;
				}
				break;
			default:
				break;
		}
	}

	return parsed;
}

export function parseRunEvents(events: RunEvent[]): ParsedRunEvents {
	return parseEvents(events);
}

export function resolveScheduleDateMs(run: RunDetail, events: RunEvent[]): number | undefined {
	const fromEvents = parseEvents(events).scheduleDateMs;
	if (fromEvents != null) return fromEvents;
	return parseTime(run.scheduleDate) ?? undefined;
}

export function formatCountdown(totalSec: number): string {
	const sec = Math.max(0, Math.ceil(totalSec));
	const minutes = Math.floor(sec / 60);
	const seconds = sec % 60;
	return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function formatStepDuration(ms: number): string {
	if (ms < 1000) return '<1с';
	const totalSec = Math.round(ms / 1000);
	if (totalSec < 60) return `${totalSec}с`;
	const minutes = Math.floor(totalSec / 60);
	const seconds = totalSec % 60;
	if (seconds === 0) return `${minutes}м`;
	return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function formatLocalTime(ms: number): string {
	return new Date(ms).toLocaleTimeString('ru-RU', {
		hour: '2-digit',
		minute: '2-digit',
	});
}

function isAccountDeleteDisabled(parsed: ParsedEvents): boolean {
	return parsed.cleanupSkipped === true || parsed.accountDeleteEnabled === false;
}

function resolveActiveStepId(
	run: RunDetail,
	nowMs: number,
	scheduleDateMs: number | undefined,
): PipelineStepId {
	if (run.status === 'queued' || run.phase === 'queued') return 'queue';

	if (run.status === 'failed' || run.status === 'cancelled') {
		return mapPhaseToStepId(run.phase, nowMs, scheduleDateMs);
	}

	if (run.status === 'completed_empty') return 'done';

	if (TERMINAL_RUN_STATUSES.has(run.status)) return 'done';

	return mapPhaseToStepId(run.phase, nowMs, scheduleDateMs);
}

function mapPhaseToStepId(
	phase: RunPhase,
	nowMs: number,
	scheduleDateMs: number | undefined,
): PipelineStepId {
	switch (phase) {
		case 'validating':
			return 'session';
		case 'preparing':
			return 'prep';
		case 'scheduling':
			return 'scheduling';
		case 'waiting':
			if (scheduleDateMs != null && nowMs < scheduleDateMs) return 'wait_send';
			return 'cleanup';
		case 'delivered':
			return 'cleanup';
		case 'cleaning':
			return 'cleanup';
		case 'queued':
			return 'queue';
		default:
			return 'queue';
	}
}

function resolveCounts(run: RunDetail, parsed: ParsedEvents) {
	const counters = run.counters;
	const audienceTarget =
		counters?.audienceTarget ?? parsed.targetCount ?? parsed.lastScheduleProgress?.target ?? 0;
	const scheduledCount =
		counters?.scheduledCount ??
		run.scheduledCount ??
		parsed.lastScheduleProgress?.scheduled ??
		0;
	const deliveredCount =
		counters?.deliveredCount ??
		run.deliveredCount ??
		parsed.lastDeliveryProgress?.delivered ??
		0;
	const scheduledForDelivery =
		parsed.lastDeliveryProgress?.scheduled ?? scheduledCount ?? audienceTarget;

	return { audienceTarget, scheduledCount, deliveredCount, scheduledForDelivery };
}

function buildStepStates(
	activeStepId: PipelineStepId,
	run: RunDetail,
	parsed: ParsedEvents,
	scheduleDateMs: number | undefined,
): PipelineStep[] {
	const activeIndex = STEP_DEFINITIONS.findIndex(step => step.id === activeStepId);
	const isEmpty = run.status === 'completed_empty';
	const isFailed = run.status === 'failed';
	const isCancelled = run.status === 'cancelled';
	const isTerminal = TERMINAL_RUN_STATUSES.has(run.status);

	const stepStarts: Partial<Record<PipelineStepId, number>> = {
		queue: parseTime(run.createdAt) ?? parsed.runStartedAt,
		session: parsed.runStartedAt ?? parseTime(run.startedAt),
		prep: parsed.audienceResolvedAt ?? parsed.runStartedAt ?? parseTime(run.startedAt),
		scheduling: parsed.burstScheduledAt,
		wait_send: parsed.scheduleCompleteAt ?? parsed.burstScheduledAt,
		cleanup: parsed.burstFiredAt ?? parsed.deliveryCompleteAt ?? parsed.cleanupStartedAt,
		done: parsed.runCompletedAt ?? parseTime(run.finishedAt),
	};

	const stepEnds: Partial<Record<PipelineStepId, number>> = {
		queue: parsed.runStartedAt ?? parseTime(run.startedAt),
		session: parsed.audienceResolvedAt ?? parsed.burstScheduledAt,
		prep: parsed.burstScheduledAt ?? parsed.audienceResolvedAt,
		scheduling: parsed.scheduleCompleteAt,
		wait_send: scheduleDateMs,
		cleanup: parsed.runCompletedAt ?? parseTime(run.finishedAt),
	};

	return STEP_DEFINITIONS.map((def, index) => {
		let state: PipelineStepState = 'pending';

		if (isEmpty && SKIPPABLE_STEP_IDS.has(def.id)) {
			state = 'skipped';
		} else if (isCancelled && def.id === activeStepId) {
			state = 'cancelled';
		} else if (isFailed && def.id === activeStepId) {
			state = 'failed';
		} else if ((isFailed || isCancelled) && index < activeIndex) {
			state = 'completed';
		} else if (isTerminal && def.id === 'done') {
			state = 'completed';
		} else if (isTerminal && def.id !== 'done') {
			state = isEmpty && SKIPPABLE_STEP_IDS.has(def.id) ? 'skipped' : 'completed';
		} else if (def.id === activeStepId) {
			state = 'active';
		} else if (index < activeIndex) {
			state = 'completed';
		}

		const start = stepStarts[def.id];
		const end = stepEnds[def.id];
		let durationMs: number | undefined;
		if (state === 'completed' && start != null && end != null && end >= start) {
			durationMs = end - start;
		}

		return {
			id: def.id,
			label: def.label,
			state,
			durationMs,
			durationLabel: durationMs != null ? formatStepDuration(durationMs) : undefined,
		};
	});
}

function buildHero(
	run: RunDetail,
	activeStepId: PipelineStepId,
	parsed: ParsedEvents,
	nowMs: number,
	scheduleDateMs: number | undefined,
	counts: ReturnType<typeof resolveCounts>,
): PipelineHero {
	const { audienceTarget, scheduledCount, deliveredCount, scheduledForDelivery } = counts;

	if (run.status === 'completed_empty') {
		return {
			title: 'Сессия без аудитории',
			subtitle: 'Рассылка не выполнялась, web_delete не запускался',
			variant: 'muted',
		};
	}

	if (run.status === 'completed' || run.status === 'completed_with_warnings') {
		const delivered = deliveredCount || scheduledCount;
		const scheduled = scheduledForDelivery || scheduledCount || audienceTarget;
		const started = parseTime(run.startedAt) ?? parseTime(run.createdAt);
		const finished = parseTime(run.finishedAt) ?? nowMs;
		const totalDuration =
			started != null && finished != null ? formatStepDuration(finished - started) : undefined;
		const partialSchedule =
			parsed.partialSchedule === true ||
			(audienceTarget > 0 && delivered > 0 && delivered < audienceTarget);
		const counters = run.counters;
		if (partialSchedule && audienceTarget > 0) {
			const planned = scheduledCount || parsed.scheduledAtAbort || delivered;
			const reasonLabel = formatScheduleAbortReason(parsed.abortReason);
			const skipParts: string[] = [];
			if ((counters?.skippedPeerFlood ?? 0) > 0) {
				skipParts.push(`Flood ${counters?.skippedPeerFlood}`);
			}
			if ((counters?.skippedNoRights ?? 0) > 0) {
				skipParts.push(`Нет прав ${counters?.skippedNoRights}`);
			}
			if ((counters?.skippedBanned ?? 0) > 0) {
				skipParts.push(`Бан ${counters?.skippedBanned}`);
			}
			let subtitle = `Запланировано ${planned} из ${audienceTarget}`;
			if (skipParts.length > 0) {
				subtitle = `${subtitle}. Ошибки: ${skipParts.join(', ')}`;
			} else if (planned < audienceTarget) {
				subtitle = `Запланировано ${planned} из ${audienceTarget}`;
			}
			if (reasonLabel) subtitle = `${subtitle}. ${reasonLabel}`;
			return {
				title: `Готово · ${delivered} из ${audienceTarget}`,
				subtitle,
				eta: totalDuration ? `~${totalDuration}` : undefined,
				variant: 'warning',
			};
		}
		return {
			title: `Готово · ${delivered} из ${scheduled}`,
			subtitle:
				parsed.accountDeleted === false
					? 'Рассылка завершена, аккаунт Telegram не удалялся'
					: run.status === 'completed_with_warnings'
						? 'Завершено с предупреждениями'
						: 'Рассылка завершена',
			eta: totalDuration ? `~${totalDuration}` : undefined,
			variant: 'success',
		};
	}

	if (run.status === 'cancelled') {
		return {
			title: 'Запуск отменён',
			variant: 'muted',
		};
	}

	if (run.status === 'failed') {
		return {
			title: 'Ошибка выполнения',
			subtitle: run.errorMessage ?? undefined,
			eta: run.resumeHint ?? undefined,
			variant: 'error',
		};
	}

	switch (activeStepId) {
		case 'queue':
			return {
				title: 'В очереди',
				subtitle: 'Ожидаем свободный worker',
				variant: 'active',
			};
		case 'session':
			return {
				title: 'Проверяем сессию',
				subtitle: 'Telegram-авторизация и прокси',
				variant: 'active',
			};
		case 'prep':
			return {
				title: 'Подготовка рассылки',
				subtitle:
					audienceTarget > 0
						? `Аудитория: ${audienceTarget} получателей`
						: 'Канал, payload и аудитория',
				variant: 'active',
			};
		case 'scheduling': {
			const target = audienceTarget || parsed.lastScheduleProgress?.target || 0;
			const scheduled = scheduledCount || parsed.lastScheduleProgress?.scheduled || 0;
			const remaining = Math.max(0, target - scheduled);
			const estimateSec = parsed.postingEstimateSec ?? remaining * 3;
			return {
				title: 'Планируем отложенные сообщения',
				subtitle: target > 0 ? `${scheduled} из ${target} в Telegram` : undefined,
				eta: remaining > 0 && estimateSec > 0 ? `~${formatCountdown(estimateSec)} до конца этапа` : undefined,
				progress: target > 0 ? { value: scheduled, max: target } : undefined,
				variant: 'active',
			};
		}
		case 'wait_send': {
			const countdownSec =
				scheduleDateMs != null ? (scheduleDateMs - nowMs) / 1000 : undefined;
			return {
				title: 'Сообщения уже в TG',
				subtitle: 'Ждём время отправки — это не задержка бага',
				eta:
					scheduleDateMs != null && countdownSec != null
						? `до ${formatLocalTime(scheduleDateMs)} (~${formatCountdown(countdownSec)})`
						: undefined,
				variant: 'active',
			};
		}
		case 'cleanup':
			if (parsed.cleanupSkipped) {
				return {
					title: 'Сессия сохранена',
					subtitle: 'Самоудаление отключено оператором',
					variant: 'success',
				};
			}
			if (run.phase === 'delivered' && parsed.burstFiredAt != null) {
				return {
					title: 'Залп отправлен',
					subtitle:
						parsed.accountDeleteEnabled === false
							? 'Сессия останется активной'
							: 'Запускаем самоудаление аккаунта Telegram',
					variant: 'active',
				};
			}
			if (parsed.accountDeleteEnabled === false) {
				return {
					title: 'Сессия сохранена',
					subtitle: 'Самоудаление отключено оператором',
					variant: 'success',
				};
			}
			return {
				title: 'Самоудаление аккаунта Telegram',
				subtitle: 'web_delete на my.telegram.org',
				variant: 'active',
			};
		case 'done':
			return {
				title: 'Завершение',
				variant: 'success',
			};
		default:
			return { title: 'Выполняется', variant: 'active' };
	}
}

function buildNextSteps(
	activeStepId: PipelineStepId,
	scheduleDateMs: number | undefined,
	run: RunDetail,
	parsed: ParsedEvents,
): PipelineNextStep[] {
	if (TERMINAL_RUN_STATUSES.has(run.status)) return [];

	const steps: PipelineNextStep[] = [];
	const activeIndex = STEP_DEFINITIONS.findIndex(step => step.id === activeStepId);

	for (let i = activeIndex + 1; i < STEP_DEFINITIONS.length; i += 1) {
		const step = STEP_DEFINITIONS[i];
		if (run.status === 'completed_empty' && SKIPPABLE_STEP_IDS.has(step.id)) continue;

		switch (step.id) {
			case 'wait_send':
				if (scheduleDateMs != null) {
					steps.push({
						label: `Ждём отправку до ${formatLocalTime(scheduleDateMs)}`,
					});
				} else {
					steps.push({ label: 'Ждём время отправки в Telegram' });
				}
				break;
			case 'cleanup':
				if (!isAccountDeleteDisabled(parsed)) {
					steps.push({ label: 'Самоудаление аккаунта Telegram (~1–2 мин)' });
				}
				break;
			case 'done':
				steps.push({ label: 'Итоговый отчёт' });
				break;
			default:
				break;
		}
		if (steps.length >= 3) break;
	}

	return steps;
}

export function deriveRunPipelineView(
	run: RunDetail,
	events: RunEvent[],
	now: Date = new Date(),
): RunPipelineView {
	const nowMs = now.getTime();
	const parsed = parseEvents(events);
	const scheduleDateMs = resolveScheduleDateMs(run, events);
	const activeStepId = resolveActiveStepId(run, nowMs, scheduleDateMs);
	const counts = resolveCounts(run, parsed);
	const isLive =
		!TERMINAL_RUN_STATUSES.has(run.status) &&
		(activeStepId === 'wait_send' || activeStepId === 'cleanup');

	return {
		hero: buildHero(run, activeStepId, parsed, nowMs, scheduleDateMs, counts),
		nextSteps: buildNextSteps(activeStepId, scheduleDateMs, run, parsed),
		steps: buildStepStates(activeStepId, run, parsed, scheduleDateMs),
		isLive,
		activeStepId,
	};
}
